import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type CustomerListResponse,
  type DayPassDto,
  type DayPassOfferDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { OpenTabPicker } from '@/features/tabs/OpenTabPicker';
import { api, ApiError } from '@/lib/api';
import {
  buildSellPayload,
  canSubmitSale,
  previewValidUntil,
  sellDayPass,
  type PassHolder,
} from './dayPasses';

/**
 * Sell a Boot Camp pass (SPEC §6.1).
 *
 * The panel answers the two questions a customer asks at the counter before
 * they hand over money — what does it cost, and when does it run out — from the
 * OFFER, not from anything typed here. There is no price field, and there is
 * nowhere to put one: the server prices the sale off the offer it names.
 *
 * With `enableBootCampOffer` off the submit is disabled with the reason
 * printed, rather than live and refused by the server. A button that does
 * nothing is worse than one that is visibly off and says why.
 */
export function SellPassDrawer({
  offers,
  enabled,
  closesAt,
  nowMs,
  currencySymbol,
  onClose,
  onSold,
}: {
  /** Only the offers that are on sale — the page filters them. */
  offers: readonly DayPassOfferDto[];
  enabled: boolean;
  closesAt: string;
  nowMs: number;
  currencySymbol: string;
  onClose: () => void;
  onSold: (pass: DayPassDto) => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const canSearchCustomers = can('customers.manage');

  const [offerId, setOfferId] = useState<string | null>(offers[0]?.id ?? null);
  const [holder, setHolder] = useState<PassHolder>({ kind: 'walkIn', name: '' });
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState<{ id: string; fullName: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const offer = offers.find((candidate) => candidate.id === offerId) ?? null;
  const money = (value: string) =>
    formatIqd(fromMillidinars(value), { currencySymbol, locale: i18n.language });

  // Debounced registered-customer search. Wired only when the permission
  // exists — the server would refuse `/customers` anyway, so the panel offers
  // nothing rather than failing quietly.
  useEffect(() => {
    if (!canSearchCustomers || holder.kind !== 'account') return;
    const query = customerSearch.trim();
    if (!query) {
      setCustomerOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const response = await api.get<CustomerListResponse>(
            `/customers?search=${encodeURIComponent(query)}&pageSize=8`,
          );
          setCustomerOptions(response.rows.map(({ id, fullName }) => ({ id, fullName })));
        } catch {
          setCustomerOptions([]);
        }
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [canSearchCustomers, customerSearch, holder.kind]);

  const runsUntil = offer ? previewValidUntil(offer, nowMs, closesAt) : null;
  const ready = canSubmitSale(offerId, holder, enabled) && offer !== null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!offerId || !ready) return;
    setError(null);
    setSubmitting(true);
    try {
      onSold(await sellDayPass(buildSellPayload(offerId, holder)));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('dayPasses.sell.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const holderTab = (kind: PassHolder['kind'], label: string, disabled = false) => (
    <button
      type="button"
      key={kind}
      data-holder-kind={kind}
      disabled={disabled}
      aria-pressed={holder.kind === kind}
      onClick={() =>
        setHolder(
          kind === 'walkIn'
            ? { kind: 'walkIn', name: '' }
            : kind === 'tab'
              ? { kind: 'tab', tabId: '', name: '' }
              : { kind: 'account', customerAccountId: '', name: '' },
        )
      }
      className={[
        'flex-1 rounded-full px-3 py-1.5 text-sm transition-colors',
        holder.kind === kind
          ? 'bg-primary text-primary-fg'
          : 'border border-subtle text-content-secondary hover:bg-hover',
        disabled ? 'cursor-not-allowed opacity-50' : '',
      ].join(' ')}
    >
      {label}
    </button>
  );

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('dayPasses.sell.title')}
        className="fixed inset-y-0 z-50 flex w-full max-w-md flex-col border-s border-subtle bg-ground shadow-2xl"
        style={{ insetInlineEnd: 0 }}
      >
        <header className="flex items-center gap-3 border-b border-subtle p-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('dayPasses.sell.cancel')}
            className="rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
          <h2 className="flex-1 truncate text-base font-semibold text-content">
            {t('dayPasses.sell.title')}
          </h2>
        </header>

        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="flex min-h-0 flex-1 flex-col overflow-auto p-4"
        >
          {!enabled ? (
            <p
              data-boot-camp-off
              role="alert"
              className="mb-4 rounded-control border border-subtle bg-raised px-3 py-2 text-sm text-content-secondary"
            >
              {t('dayPasses.sell.disabledReason')}
            </p>
          ) : null}

          {offers.length === 0 ? (
            <p className="text-sm text-content-muted">{t('dayPasses.sell.noActiveOffers')}</p>
          ) : (
            <>
              <label className="mb-4 block">
                <span className="mb-1 block text-sm text-content-secondary">
                  {t('dayPasses.sell.offer')}
                </span>
                <select
                  value={offerId ?? ''}
                  onChange={(event) => setOfferId(event.target.value || null)}
                  className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
                >
                  <option value="">{t('dayPasses.sell.chooseOffer')}</option>
                  {offers.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </option>
                  ))}
                </select>
              </label>

              <div className="mb-4">
                <span className="mb-1 block text-sm text-content-secondary">
                  {t('dayPasses.sell.holder')}
                </span>
                <div className="flex gap-2">
                  {holderTab('walkIn', t('dayPasses.sell.walkIn'))}
                  {holderTab('tab', t('dayPasses.sell.existingTab'))}
                  {holderTab('account', t('dayPasses.sell.account'), !canSearchCustomers)}
                </div>
              </div>

              {holder.kind === 'walkIn' ? (
                <input
                  value={holder.name}
                  onChange={(event) => setHolder({ kind: 'walkIn', name: event.target.value })}
                  maxLength={120}
                  placeholder={t('dayPasses.sell.namePlaceholder')}
                  aria-label={t('dayPasses.sell.namePlaceholder')}
                  className="mb-4 w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
                />
              ) : null}

              {holder.kind === 'tab' ? (
                <div className="mb-4">
                  <OpenTabPicker
                    value={holder.tabId || null}
                    onChange={(tabId, tab) => setHolder({ kind: 'tab', tabId, name: tab.name })}
                    // LIVE tabs only, like every other picker (2026-09-23). The
                    // default reads every OPEN tab, and a party paid up and
                    // never closed is still open: selling a pass onto that tab
                    // brings it back to life beside the party's real one — the
                    // cafe's second 'Yaran staff' (owner, 2026-09-23). Anyone
                    // with nothing running is a walk-in or an account here.
                    source="live"
                  />
                </div>
              ) : null}

              {holder.kind === 'account' ? (
                <div className="mb-4">
                  <input
                    value={customerSearch}
                    onChange={(event) => setCustomerSearch(event.target.value)}
                    placeholder={t('dayPasses.sell.searchPlaceholder')}
                    aria-label={t('dayPasses.sell.searchPlaceholder')}
                    className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
                  />
                  <ul className="mt-2 max-h-48 space-y-1 overflow-auto">
                    {customerOptions.map((option) => (
                      <li key={option.id}>
                        <button
                          type="button"
                          onClick={() =>
                            setHolder({
                              kind: 'account',
                              customerAccountId: option.id,
                              name: option.fullName,
                            })
                          }
                          aria-pressed={holder.customerAccountId === option.id}
                          className={`w-full rounded-control px-3 py-2 text-start text-sm ${
                            holder.customerAccountId === option.id
                              ? 'bg-primary text-primary-fg'
                              : 'border border-subtle bg-input text-content hover:bg-hover'
                          }`}
                        >
                          {option.fullName}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* What the customer is buying, in the two terms they care about.
                  Both come from the offer, never from this form. */}
              {offer ? (
                <dl
                  data-sale-preview
                  className="mb-4 grid grid-cols-2 gap-2 rounded-control bg-raised px-3 py-3 text-sm"
                >
                  <dt className="text-content-secondary">{t('dayPasses.sell.charge')}</dt>
                  <dd className="tabular text-end font-medium text-content">
                    {money(offer.priceMd)}
                  </dd>
                  <dt className="text-content-secondary">{t('dayPasses.sell.runsUntil')}</dt>
                  <dd className="tabular text-end text-content">
                    {runsUntil?.toLocaleTimeString(i18n.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {offer.durationMinutes === null ? ` (${t('dayPasses.sell.untilClosing')})` : ''}
                  </dd>
                </dl>
              ) : null}
            </>
          )}

          {error ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-auto flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
            >
              {t('dayPasses.sell.cancel')}
            </button>
            <button
              type="submit"
              disabled={!ready || submitting}
              title={enabled ? undefined : t('dayPasses.sell.disabledReason')}
              className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('common.loading') : t('dayPasses.sell.submit')}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
